import { Grid } from "@mui/material";
import { useEffect, useReducer } from "react"
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next"
import CustomAlert from "../../componentes/alerts/CustomAlert"
import InputField from "../../componentes/form-fields/InputField/InputField";
import GridInputRow from "../../componentes/form-grids/GridInputRow";
import GridItemContainer from "../../componentes/form-grids/GridItemContainer"
import PrintControlService from "./PrintControlService"
import { IconLabelSwitch } from "../../componentes/iconSwitch/IconLabelSwitch";
import { IconSwitch } from "../../componentes/iconSwitch/IconSwitch";
import { DefaultDatagrid } from "../../componentes/datagrid/DefaultDatagrid";
import { DefaultAutocomplete } from "../../componentes/autocomplete/DefaultAutocomplete";
import { DefaultScreenAccordion } from "../../componentes/layouts/components/DefaultScreenAccordion";
import DefaultChip from '../../componentes/chip/DefaultChip';
import { Typography } from "@material-ui/core";
import DefaultButton from "../../componentes/button/DefaultButton";

/**
 * Author : Lucas
 * 04/12/2026
*/
const columnsPrint = (t) => [
    { field: "id", flex: 0.5, visible: false },
    {
        field: "id",
        textAlign: 'center',
        headerTemplate: () => {
            return <IconLabelSwitch isHeader={true} labelName="ids" name="id" />;
        },
        template: (props) => {
            return (
                <>
                    <DefaultChip
                        style="standard"
                        label={<Typography
                            variant="body3"
                            style={{ whiteSpace: 'normal' }}>{props.id}
                        </Typography>}
                    />
                </>
            )
        }
    },
    {
        field: "printName",
        textAlign: "center",
        headerTemplate: () => {
            return <IconLabelSwitch isHeader={true} labelName="printName" name="company" />;
        },
        template: (props) => {
            return (
                <>
                    <DefaultChip
                        style="grey"
                        label={<Typography
                            variant="body3"
                            style={{ whiteSpace: 'normal' }}>{props.printName}
                        </Typography>}
                    />
                </>
            )
        }
    },
    {
        field: "status",
        textAlign: "center",
        headerTemplate: () => {
            return <IconLabelSwitch isHeader={true} labelName="status" />;
        },
        flex: 0.5,
        template: (props) => {
            return (
                <>
                    {props.status ? (
                        <IconSwitch name="active" />
                    ) : (
                        <IconSwitch name="canceled" />
                    )}
                </>
            );
        },
    },
];

export const PrintRegister = ({ props, updateId }) => {

    const { t } = useTranslation();
    const { control } = useForm();
    const userId = sessionStorage.getItem("userId");
    const initialState = {
        id: 0,
        printName: "",
        printRows: [],
        loggedUserId: userId,
        status: 1,
        printerStatus: null,
        success: false,
        error: false,
    };

    const reducer = (state, action) => {
        switch (action.type) {
            case 'update':
                return { ...state, ...action.data }
            case "clear":
                return initialState
            case 'success':
                return { ...state, success: true, error: false }
            case 'error':
                return { ...state, success: false, error: true }
            case 'close_alert':
                return { ...state, success: false, error: false }
            default:
                return new Error();
        }
    }

    const [PrintControl, dispatch] = useReducer(reducer, initialState);
    const { id, printName, printerStatus, status } = PrintControl
    const { success, error, printRows, ...data } = PrintControl

    useEffect(() => {
        updateId({ id: id })
    }, [id])

    const handleChange = (event) => {
        const { name, value } = event.target;
        dispatch({ type: "update", data: { [name]: value } });
    };

    const handleSearch = () => {
        PrintControlService
            .getPrinter(data)
            .then((response) => {
                if (response.status === 200) {
                    dispatch({ type: 'update', data: { printRows: response.data } })
                }
            })
            .catch((e) => dispatch({ type: 'update', data: { printRows: [] } }));
    }
    const canSave = () => printName !== "" && status !== 2;

    const handleSaveUpdate = () => {
        if (canSave()) {
            if (id) {
                PrintControlService
                    .updatePrinter(data)
                    .then((response) => {
                        if (response.status === 200) {
                            dispatch({ type: 'success' })
                            handleSearch();
                        }
                    })
                    .catch((e) => dispatch({ type: 'error' }));
            }
            else {
                PrintControlService
                    .createPrinter(data)
                    .then((response) => {
                        if (response.status === 200) {
                            dispatch({ type: 'update', data: { success: true, error: false, id: response.data.id, message: t("noDataExport") } })
                            handleSearch();
                        }
                    })
                    .catch((e) => dispatch({ type: 'error' }));
            }
        } else {
            dispatch({ type: 'error' })
        }

    }

    const handleCancelUpdate = () => {
        const payload = { ...data, status: 0 };

        PrintControlService
            .updatePrinter(payload)
            .then((response) => {
                if (response.status === 200) {
                    dispatch({ type: 'success' });
                    handleSearch();
                }
            })
            .catch(() => dispatch({ type: 'error' }));
    };


    const handleCellClickPrint = (data) => {
        dispatch({
            type: 'update', data: {
                id: data.id,
                printName: data.printName,
                clientName: data.clientName,
                status: data.status,
                printerStatus: data.status,
            }
        })
    }

    useEffect(() => {
        handleSearch();
    }, [])

    return (
        <>
            <DefaultScreenAccordion
                title={t("printer")}
                isRegister
                fields={
                    <GridItemContainer>
                        <CustomAlert
                            success={success}
                            error={error}
                            handleCloseAlert={() => dispatch({ type: 'close_alert' })}
                        />
                        <Grid item xs={12} sm={6} md={8}>
                            <GridInputRow>
                                <Grid item xs={12} sm={6} md={4}>
                                    <InputField
                                        iconLabelSwitch={
                                            <IconLabelSwitch name="company" labelName="printName" />
                                        }
                                        name="printName"
                                        control={control}
                                        defaultValue=""
                                        onChange={handleChange}
                                        value={printName}
                                        inputProps={{ maxLength: 50 }}
                                        error={error && printName === ""}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <DefaultAutocomplete
                                        name={"status"}
                                        onChange={handleChange}
                                        value={status}
                                        label={t("status")}
                                        defaultSize
                                        items={[
                                            { id: 0, name: t("canceled"), status: 1 },
                                            { id: 1, name: t("active"), status: 1 },
                                            { id: 2, name: t("all"), status: 1 },
                                        ]}
                                        error={error && status === 2}
                                    />
                                </Grid>
                            </GridInputRow>
                            <GridInputRow>
                                <Grid item xs={12} sm={6} md={3}>
                                    <DefaultButton search className="primaryButton" onClick={handleSearch} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <DefaultButton clear className="secondaryButton" onClick={() => dispatch({ type: 'clear' })} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <DefaultButton save className="primaryButton" onClick={handleSaveUpdate} />
                                </Grid>
                                {/* {id !== 0 && printerStatus !== 0 && <Grid item xs={12} sm={6} md={3}>
                                    <DefaultButton labelName="Cancelar impressora" className="errorButton" onClick={handleCancelUpdate} />
                                </Grid>} */}
                            </GridInputRow>
                        </Grid>

                        {(printRows.length > 0) ? (
                            <GridInputRow>
                                <Grid item xs={12}>
                                    <DefaultDatagrid
                                        columns={columnsPrint(t)}
                                        dataSource={printRows}
                                        allowPaging={true}
                                        cellSelected={(args, grid) =>
                                            handleCellClickPrint(args.data)
                                        } />
                                </Grid>
                            </GridInputRow>
                        ) : null}
                    </GridItemContainer>
                } />
        </>
    );
}